import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { TrackingApi } from 'src/api';
import { getCanonicalSessionType, WebSessionType } from 'src/api/learningSessions/types';
import { ROUTES } from 'src/constants/routes.const';
import { useAudioContext, useAudioPlayback } from 'src/context/AudioContext';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';

type UseExitSessionArgs = {
  sessionType: WebSessionType;
  sendCurrentLearningProgress: () => Promise<void>;
  analyticsSessionId?: string;
  trackingData?: Record<string, string>;
};

export const useExitSession = ({
  sessionType,
  sendCurrentLearningProgress,
  analyticsSessionId,
  trackingData,
}: UseExitSessionArgs) => {
  const { safePush } = useSafePush();
  const { user } = useUserContext();
  const { query } = useRouter();
  const backUrl = query.back ? String(query.back) : undefined;
  const { playingAudioUrl } = useAudioContext();
  const { stopAll } = useAudioPlayback(playingAudioUrl);

  return useCallback(async () => {
    stopAll(); // stops any lengthy audio if it's still in progress

    void TrackingApi.trackUserAction(
      'learning_session_exited',
      'learning_session',
      getLanguagePairId(),
      !!user?.isPro,
      {
        ...trackingData,
        ...(analyticsSessionId && { session_id: analyticsSessionId }),
        session_type: getCanonicalSessionType(sessionType),
      },
    );

    await sendCurrentLearningProgress();
    await safePush(backUrl || ROUTES.dashboard);
  }, [
    analyticsSessionId,
    backUrl,
    safePush,
    sendCurrentLearningProgress,
    sessionType,
    stopAll,
    trackingData,
    user?.isPro,
  ]);
};
